
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import messageService from '../services/endpoints/messageService';
import coachService from '../services/endpoints/coachService';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { Message, CoachClient, MyCoach, UnreadMessageSummary } from '../types';

const Messages: React.FC = () => {
  const { user } = useCurrentUser();
  const [searchParams] = useSearchParams();
  const initialRel = searchParams.get('rel');

  const isCoach = user?.isCoach ?? false;

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        Please sign in to view messages.
      </div>
    );
  }

  return isCoach
    ? <CoachMessagesView userId={user.userId} initialRelId={initialRel} />
    : <AthleteMessagesView userId={user.userId} />;
};

// ── Athlete View ─────────────────────────────────────────────────────────────
// Athlete has exactly one coach relationship — show the thread directly.

const AthleteMessagesView: React.FC<{ userId: string }> = ({ userId }) => {
  const { data: coaches = [], isLoading } = useQuery<MyCoach[]>({
    queryKey: ['my-coaches'],
    queryFn: () => coachService.getMyCoaches(),
  });
  const coach = coaches[0] ?? null;

  if (isLoading) return <PageLoader />;

  if (!coach) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="text-4xl mb-4">👤</div>
        <h2 className="text-xl text-gray-300 mb-2">No Coach Connected</h2>
        <p className="text-gray-500 text-center max-w-sm">
          Go to <a href="/settings" className="text-gold-500 hover:text-gold-400">Settings</a> and
          share your invite code with a coach to start messaging.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-120px)]">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gold-500">💬 Messages</h1>
        <p className="text-gray-400 text-sm mt-1">Conversation with Coach {coach.coachName}</p>
      </div>
      <ThreadPanel
        relationshipId={coach.relationshipId}
        partnerName={coach.coachName}
        currentUserId={userId}
      />
    </div>
  );
};

// ── Coach View ───────────────────────────────────────────────────────────────
// Coach sees athlete list on the left; clicking an athlete opens their thread.

const CoachMessagesView: React.FC<{ userId: string; initialRelId: string | null }> = ({
  userId,
  initialRelId,
}) => {
  const { data: clients = [], isLoading } = useQuery<CoachClient[]>({
    queryKey: ['coach-clients'],
    queryFn: () => coachService.getClients(),
  });

  // Reuse the same cached query key as AlertContext — no extra network call
  const { data: unreadSummaries = [] } = useQuery<UnreadMessageSummary[]>({
    queryKey: ['unread-messages'],
    queryFn: () => messageService.getUnreadSummary(),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  const [selectedRelId, setSelectedRelId] = useState<string | null>(initialRelId);

  // Auto-select first client if initialRelId points to a known client, else first in list
  useEffect(() => {
    if (!selectedRelId && clients.length > 0) setSelectedRelId(clients[0].relationshipId);
    if (initialRelId && clients.length > 0) setSelectedRelId(initialRelId);
  }, [clients, initialRelId, selectedRelId]);

  const selectedClient = clients.find(c => c.relationshipId === selectedRelId);

  // Count unread per relationship (only for non-selected athletes)
  const unreadCountFor = (relId: string): number => {
    if (relId === selectedRelId) return 0; // currently open → already marking read
    return unreadSummaries
      .filter(s => s.relationshipId === relId)
      .reduce((sum, s) => sum + s.unreadCount, 0);
  };

  if (isLoading) return <PageLoader />;

  if (clients.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="text-4xl mb-4">🏋️</div>
        <h2 className="text-xl text-gray-300 mb-2">No Athletes Connected</h2>
        <p className="text-gray-500 text-center max-w-sm">
          Athletes can send you their invite code via
          <a href="/settings" className="text-gold-500 hover:text-gold-400"> Settings → My Athletes</a>.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gold-500">💬 Messages</h1>
      <div className="flex gap-4 h-[calc(100vh-160px)]">
        {/* ── Athlete sidebar ── */}
        <div className="w-56 flex-shrink-0 bg-dark-800 rounded-xl border border-dark-700 overflow-y-auto">
          <div className="px-3 py-3 border-b border-dark-700 text-xs text-gray-500 uppercase tracking-wider">
            Athletes
          </div>
          {clients.map(c => {
            const unread = unreadCountFor(c.relationshipId);
            return (
              <button
                key={c.relationshipId}
                onClick={() => setSelectedRelId(c.relationshipId)}
                className={`w-full text-left px-4 py-3 text-sm transition-colors border-b border-dark-700/50 ${
                  selectedRelId === c.relationshipId
                    ? 'bg-dark-700 text-gold-400 font-medium'
                    : 'text-gray-300 hover:bg-dark-700 hover:text-white'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">{c.athleteName}</span>
                  {unread > 0 && (
                    <span className="flex-shrink-0 min-w-[20px] h-5 px-1.5 bg-gold-500 text-black text-xs font-bold rounded-full flex items-center justify-center leading-none">
                      {unread}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* ── Thread panel ── */}
        <div className="flex-1 min-w-0">
          {selectedClient ? (
            <ThreadPanel
              relationshipId={selectedClient.relationshipId}
              partnerName={selectedClient.athleteName}
              currentUserId={userId}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              Select an athlete to start messaging.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Thread Panel ─────────────────────────────────────────────────────────────
// The actual chat UI used by both athlete and coach views.

const ThreadPanel: React.FC<{
  relationshipId: string;
  partnerName: string;
  currentUserId: string;
}> = ({ relationshipId, partnerName, currentUserId }) => {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { data: messages = [], isLoading } = useQuery<Message[]>({
    queryKey: ['messages', relationshipId],
    queryFn: () => messageService.getMessages(relationshipId),
    // Automatically refetches every 30 s for near-real-time feel
    refetchInterval: 30_000,
    // Also triggers when switching tabs back in
    refetchOnWindowFocus: true,
  });

  // Scroll to bottom whenever messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Also invalidate unread-messages bell query when thread opens
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['unread-messages'] });
  }, [relationshipId, queryClient]);

  const sendMutation = useMutation({
    mutationFn: () => messageService.sendMessage({ relationshipId, body: draft.trim() }),
    onSuccess: (newMsg) => {
      queryClient.setQueryData<Message[]>(['messages', relationshipId], (prev = []) => [
        ...prev,
        newMsg,
      ]);
      setDraft('');
      // Refresh unread bell
      queryClient.invalidateQueries({ queryKey: ['unread-messages'] });
    },
  });

  const handleSend = useCallback(() => {
    if (!draft.trim() || sendMutation.isPending) return;
    sendMutation.mutate();
  }, [draft, sendMutation]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Ctrl+Enter or Cmd+Enter sends
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full bg-dark-800 rounded-xl border border-dark-700 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-dark-700 flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-gold-500/20 flex items-center justify-center text-gold-400 font-bold text-sm">
          {partnerName.charAt(0).toUpperCase()}
        </div>
        <div>
          <div className="text-white font-medium">{partnerName}</div>
          <div className="text-xs text-gray-500">General thread</div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center h-full text-gray-500">Loading...</div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500 text-sm">
            No messages yet. Start the conversation!
          </div>
        ) : (
          messages.map((msg) => (
            <MessageBubble key={msg.messageId} msg={msg} isOwn={msg.senderUserId === currentUserId} />
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* Compose */}
      <div className="px-4 py-3 border-t border-dark-700 flex gap-3 items-end">
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message… (Ctrl+Enter to send)"
          rows={2}
          className="flex-1 resize-none bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-gold-500 transition-colors"
        />
        <button
          onClick={handleSend}
          disabled={!draft.trim() || sendMutation.isPending}
          className="px-4 py-2 bg-gold-500 hover:bg-gold-400 disabled:opacity-40 disabled:cursor-not-allowed text-black text-sm font-semibold rounded-lg transition-colors flex-shrink-0"
        >
          {sendMutation.isPending ? '...' : 'Send'}
        </button>
      </div>
    </div>
  );
};

// ── Message Bubble ────────────────────────────────────────────────────────────

const MessageBubble: React.FC<{ msg: Message; isOwn: boolean }> = ({ msg, isOwn }) => {
  const time = new Date(msg.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const date = new Date(msg.sentAt).toLocaleDateString([], { month: 'short', day: 'numeric' });

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[75%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
        {!isOwn && (
          <span className="text-xs text-gray-500 px-1">{msg.senderName}</span>
        )}
        <div
          className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
            isOwn
              ? 'bg-gold-500/20 text-white rounded-tr-sm'
              : 'bg-dark-700 text-gray-100 rounded-tl-sm'
          }`}
        >
          {msg.body}
        </div>
        <span className="text-xs text-gray-600 px-1">{date} · {time}</span>
      </div>
    </div>
  );
};

// ── Loader ───────────────────────────────────────────────────────────────────

const PageLoader = () => (
  <div className="flex items-center justify-center h-64 text-gray-400">Loading…</div>
);

export default Messages;
